import { useTranslation } from 'react-i18next';

import { formatInstant } from '@/features/inventory/registerUi';
import { useInstallationChain } from './useInstallations';

/**
 * THE RELOCATION CHAIN for one tracker (slice 30) — every assignment it has ever
 * had, newest first, each with its install record or the fact that it has none.
 *
 * Rendered inside the DEVICE row detail (`DeviceHistoryDialog`). Relocation
 * creates a new record by construction: moving a tracker opens a new assignment,
 * and the chain is that assignment history, derived — never a stored pointer.
 *
 * 🔴 NO IMEI HERE. The endpoint deliberately carries none; a tracker is named by
 * the vehicle plate it was in.
 */
export function InstallationChain({ deviceId }: { deviceId: string }) {
  const { t, i18n } = useTranslation();
  const q = useInstallationChain(deviceId);

  if (q.isPending) {
    return <p className="text-[12.5px] text-raad-muted">{t('installations.loading')}</p>;
  }
  if (q.isError || q.data === undefined) {
    return (
      <p role="alert" className="text-[12.5px] text-raad-error">
        {t('installations.error')}
      </p>
    );
  }

  const { chain } = q.data;
  if (chain.length === 0) {
    return <p className="text-[12.5px] text-raad-muted">{t('installations.chain.empty')}</p>;
  }

  return (
    <ul className="grid gap-2">
      {chain.map((e) => {
        const rec = e.installation;
        return (
          <li key={e.assignmentId} className="rounded-lg border border-raad-border p-3">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
              <span className="num text-[13px] font-semibold">{e.vehiclePlate}</span>
              <span className="text-[11.5px] text-raad-muted">
                {formatInstant(e.fromAt, i18n.language)}
                {' — '}
                {e.toAt ? formatInstant(e.toAt, i18n.language) : t('installations.chain.open')}
              </span>
            </div>
            {rec === null ? (
              <p className="mt-1 text-[12px] text-raad-muted">{t('installations.chain.noRecord')}</p>
            ) : (
              <p className="mt-1 text-[12px]">
                <span className="text-raad-muted">{t('installations.field.installerName')}: </span>
                {rec.installerName}
                {' · '}
                {rec.verifiedLiveAt ? (
                  <span className="text-raad-accent">{t('installations.status.confirmed')}</span>
                ) : (
                  <span className="text-raad-warn">{t('installations.status.unconfirmed')}</span>
                )}
              </p>
            )}
          </li>
        );
      })}
    </ul>
  );
}
